import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, clearSession, getStoredUser, registerOn401, setSession } from "./api";

const RANK = ["viewer", "presenter", "builder", "admin"];

export function roleAtLeast(role, minimum) {
  return RANK.indexOf(role) >= RANK.indexOf(minimum);
}

export function hasPermission(user, perm) {
  const r = user?.role;
  if (!r) return false;
  switch (perm) {
    case "workflow.read":
      return roleAtLeast(r, "viewer");
    case "workflow.run":
      return roleAtLeast(r, "presenter");
    case "workflow.write":
      return roleAtLeast(r, "builder");
    case "users.manage":
      return roleAtLeast(r, "admin");
    default:
      return false;
  }
}

const AuthContext = createContext({
  user: null,
  loading: true,
  refreshUser: async () => null,
  logout: async () => {},
  hasPermission: () => false,
});

// Sec-S9 (2026-05-26): cookie-based session.  We no longer need
// `loginWithToken` because the backend's /api/auth/google/callback
// already sets the HttpOnly cookie before redirecting to /workflows;
// we just call /api/auth/me to populate the React state.  The old
// `loginWithToken` is kept as a thin alias to `refreshUser` so any
// remaining callers don't break.
export function AuthProvider({ children, onUnauthorized }) {
  const [user, setUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    registerOn401(() => {
      setUser(null);
      if (typeof onUnauthorized === "function") onUnauthorized();
    });
  }, [onUnauthorized]);

  // Fetch /auth/me — works iff the eda_session cookie is set.
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setSession({ user: data });
      return data;
    } catch {
      clearSession();
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (alive) {
          setUser(data);
          setSession({ user: data });
        }
      } catch {
        if (alive) {
          clearSession();
          setUser(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* best-effort */
    }
    clearSession();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      refreshUser,
      // Backwards-compat shim — the old /auth/callback page imports this.
      // The token argument is ignored; the cookie has already been set
      // server-side by the time we get here.
      loginWithToken: refreshUser,
      logout,
      hasPermission: (perm) => hasPermission(user, perm),
    }),
    [user, loading, refreshUser, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
